import Echo from "laravel-echo";
import Pusher from "pusher-js";

import axiosInstance from "src/common/axiosInstance";

/**
 * Lazily-created Laravel Echo singleton for realtime chat.
 *
 * Realtime is opt-in: if REACT_APP_REVERB_KEY is not set the app runs exactly
 * as before (no sockets, no errors) and callers get `null`.
 *
 * The broadcasting auth request reuses axiosInstance so either the deployed
 * app session or the local developer OAuth bearer token is attached.
 */

let echoInstance;
let initialised = false;

const env = (key, fallback = "") => {
  const value = process.env[key];
  return value === undefined || value === null || value === "" ? fallback : value;
};

function createEcho() {
  const key = env("REACT_APP_REVERB_KEY");
  if (!key) return null;

  const host = env("REACT_APP_REVERB_HOST", window.location.hostname);
  const port = Number(env("REACT_APP_REVERB_PORT", "8080"));
  const scheme = env("REACT_APP_REVERB_SCHEME", "http");
  const forceTLS = scheme === "https";

  // pusher-js expects a global in some builds
  window.Pusher = Pusher;

  return new Echo({
    broadcaster: "reverb",
    key,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS,
    enabledTransports: ["ws", "wss"],
    disableStats: true,
    // keep trying if the server is briefly unavailable
    activityTimeout: 30000,
    pongTimeout: 15000,
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        axiosInstance
          .post("/broadcasting/auth", {
            socket_id: socketId,
            channel_name: channel.name,
          })
          .then((response) => callback(null, response.data))
          .catch((error) => callback(error));
      },
    }),
  });
}

/** Returns the Echo instance, or null when realtime is not configured. */
export function getEcho() {
  if (!initialised) {
    initialised = true;
    try {
      echoInstance = createEcho();
    } catch (error) {
      // Misconfiguration should never take the chat page down.
      console.warn("Realtime disabled:", error?.message || error);
      echoInstance = null;
    }
  }
  return echoInstance || null;
}

export function isRealtimeEnabled() {
  return Boolean(getEcho());
}
